import React from "react";
import { Link, Navigate } from "react-router-dom";
import { FiCheck, FiClock, FiCopy, FiPlus, FiUserCheck } from "react-icons/fi";
import PayoutBeneficiaryFlowSteps from "./PayoutBeneficiaryFlowSteps";
import { isPayoutApiMode } from "./api/payoutApiClient";
import { pickString, readSessionJson } from "./api/payoutMutationHelpers";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 last:border-b-0 md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#505E8E]">{label}</span>
      <div className="text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function PayoutAddBeneficiaryConfirm() {
  const createResult = readSessionJson<Record<string, any>>(
    "payoutBeneficiaryCreateResult",
    {},
  );
  const draft = readSessionJson<Record<string, any>>("payoutBeneficiaryDraft", {});

  if (isPayoutApiMode() && !pickString(createResult, ["beneficiary_id", "beneficiaryId", "id"])) {
    return (
      <Navigate
        to="/payouts/beneficiaries/add/verify"
        replace
        state={{
          error:
            "Beneficiary was not created. Complete Verify → Continue and watch for a red error message.",
        }}
      />
    );
  }

  const beneficiaryId =
    pickString(createResult, ["beneficiary_id", "beneficiaryId", "id"], "") ||
    "Pending ID";
  const name =
    pickString(createResult, ["name_entered", "name", "beneficiary_name"], "") ||
    draft.beneficiaryName ||
    "Beneficiary";
  const accountLast4 =
    pickString(createResult, ["account_number_last4", "accountNumberLast4"], "") ||
    String(draft.accountNumber || "").slice(-4);
  const ifsc =
    pickString(createResult, ["ifsc", "ifsc_code"], "") || draft.ifscCode || "-";
  const category =
    pickString(createResult, ["category"], "") ||
    pickString(draft.category, ["label", "value"], "Vendor");
  const accountType =
    pickString(createResult, ["account_type", "accountType"], "") ||
    pickString(draft.accountType, ["label", "value"], "Savings");
  const payoutAccount =
    pickString(draft.payoutAccount, ["label", "name"], "Payout Account");
  const rawStatus =
    pickString(
      createResult,
      ["ui_status", "verification_status", "status"],
      createResult.skipped_verification ? "Pending" : "Pending",
    ) || "Pending";
  const status =
    /verified/i.test(rawStatus) && !/unverified/i.test(rawStatus)
      ? "Verified"
      : /fail|mismatch/i.test(rawStatus)
        ? "Failed"
        : "Pending";
  const isVerified = status === "Verified";

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Confirm Beneficiary
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Beneficiary has been added
          {isVerified ? " and verified" : " (verification pending)"}.
        </p>
      </div>

      <PayoutBeneficiaryFlowSteps activeStep={4} />

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#DCFCE7] text-4xl text-[#16A34A]">
          <FiCheck />
        </div>
        <h2 className="text-[22px] font-semibold text-[#1B163B]">
          Beneficiary Added Successfully!
        </h2>
        <p className="mt-3 text-sm font-medium text-[#505E8E]">
          {name} is now available
          {isVerified
            ? " for payout transfers."
            : ". Bank name verification is still pending."}
        </p>

        <div className="mt-7 overflow-hidden rounded-[8px] border border-[#E0E5F2] text-left">
          <DetailRow
            label="Beneficiary ID"
            value={
              <span className="flex items-center justify-between gap-4">
                {beneficiaryId}
                <FiCopy className="shrink-0 text-[#505E8E]" />
              </span>
            }
          />
          <DetailRow label="Beneficiary Name" value={name} />
          <DetailRow
            label="Payout Bank Account"
            value={
              <span>
                {payoutAccount}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  **** {accountLast4 || "----"}
                </span>
              </span>
            }
          />
          <DetailRow
            label="Bank Details"
            value={
              <span>
                {accountType}
                <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                  A/C: **** {accountLast4 || "----"} | IFSC: {ifsc}
                </span>
              </span>
            }
          />
          <DetailRow
            label="Category"
            value={
              <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
                {category}
              </span>
            }
          />
          <DetailRow
            label="Status"
            value={
              <span
                className={
                  "inline-flex items-center gap-2 rounded-[5px] px-2 py-1 text-xs font-semibold " +
                  (isVerified
                    ? "bg-[#DCFCE7] text-[#16A34A]"
                    : "bg-[#FEF3C7] text-[#B45309]")
                }
              >
                {isVerified ? <FiUserCheck /> : <FiClock />}
                {status}
              </span>
            }
          />
        </div>

        <div className="mt-6 rounded-[8px] border border-[#E0E5F2] bg-white p-5 text-left">
          <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
            What's Next?
          </h3>
          <p className="mb-3 text-sm font-medium text-[#505E8E]">
            {"\u2022"} This beneficiary can now be selected while initiating a
            payout transfer.
          </p>
          <p className="text-sm font-medium text-[#505E8E]">
            {"\u2022"} You can edit bank details, category, or account mapping
            from the beneficiaries table.
          </p>
        </div>
      </section>

      <div className="mt-8 flex max-w-[760px] flex-wrap items-center justify-between gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to="/payouts/beneficiaries"
          className="flex h-10 items-center rounded-[6px] border border-[#1B163B] px-7 text-sm font-semibold text-[#1B163B]"
        >
          View Beneficiaries
        </Link>
        <Link
          to="/payouts/beneficiaries/add"
          className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
        >
          <FiPlus />
          Add Another
        </Link>
      </div>
    </div>
  );
}

export default PayoutAddBeneficiaryConfirm;
